import type { BattleStateView, Direction, UnitState } from '@tank-battle/shared';
import { buildTelegramInviteLink, getFogAt } from '@tank-battle/shared';
import { TELEGRAM_BOT_USERNAME, TELEGRAM_MINI_APP_SHORT_NAME } from '../config.js';

export type ActionMode = 'move' | 'fire' | 'rotate_turret' | 'rotate_hull';

const DIRECTION_ARROWS: Record<Direction, string> = {
  0: '↑',
  1: '→',
  2: '↓',
  3: '←',
};

export function directionArrow(facing: Direction): string {
  return DIRECTION_ARROWS[facing];
}

export function isOnTurretRay(
  ux: number,
  uy: number,
  tx: number,
  ty: number,
  facing: Direction,
): boolean {
  switch (facing) {
    case 0:
      return tx === ux && ty < uy;
    case 1:
      return ty === uy && tx > ux;
    case 2:
      return tx === ux && ty > uy;
    case 3:
      return ty === uy && tx < ux;
    default:
      return false;
  }
}

export function canFireAtCell(
  view: BattleStateView,
  unit: UnitState,
  x: number,
  y: number,
): boolean {
  if (!isOnTurretRay(unit.x, unit.y, x, y, unit.turretFacing)) return false;
  if (getFogAt(view.fog, x, y) === 'hidden') return false;
  const target = view.units.find((u) => u.hp > 0 && u.x === x && u.y === y);
  return Boolean(target && target.side !== unit.side);
}

/** Opens bot chat → «Принять вызов» (works on Telegram Web/Desktop/Mobile). */
export function getTelegramInviteLink(battleId: string): string {
  return buildTelegramInviteLink({
    botUsername: TELEGRAM_BOT_USERNAME,
    battleId,
    miniAppShortName: TELEGRAM_MINI_APP_SHORT_NAME || undefined,
  });
}

/** Direct HTTPS link (fallback for desktop browser). */
export function getWebInviteUrl(battleId: string): string {
  const base = window.location.origin + window.location.pathname;
  return `${base}?battle=${encodeURIComponent(battleId)}`;
}

/** Prefer Telegram deep link when inside Mini App. */
export function getInviteUrl(battleId: string): string {
  if (window.Telegram?.WebApp?.initData) {
    return getTelegramInviteLink(battleId);
  }
  return getWebInviteUrl(battleId);
}

export function readBattleIdFromLaunch(): string | null {
  const params = new URLSearchParams(window.location.search);
  const fromQuery =
    params.get('battle')?.trim() ?? params.get('tgWebAppStartParam')?.trim();
  if (fromQuery) return fromQuery;

  const startParam = window.Telegram?.WebApp?.initDataUnsafe?.start_param?.trim();
  if (startParam) return startParam;

  return null;
}
